/**
 * EN EL TELÉFONO, LA APP NO SE USA SIN INSTALAR.
 *
 * Lo pidió Javier así, y es una decisión fuerte: esto no es un banner que
 * estorba, es una puerta. Todo lo que decide a quién se le cierra vive en
 * `lib/instalacion.ts`, en funciones puras y con catorce pruebas, porque un
 * error acá no molesta a nadie: deja a alguien afuera de la aplicación entera.
 *
 * LAS TRES PANTALLAS, Y POR QUÉ SON TRES:
 *
 *  · ANDROID tiene un botón que instala de verdad, si el navegador ofreció el
 *    evento. Si no lo ofreció —Firefox, o Chrome que todavía no decidió— hay
 *    que decir dónde está la opción en el menú, porque un botón que no hace
 *    nada es peor que no tener botón;
 *  · iOS NO TIENE BOTÓN. Safari nunca dio una forma de pedir la instalación
 *    desde la página, así que lo único honesto es el instructivo: Compartir,
 *    y después «Agregar a inicio»;
 *  · ADENTRO DE WHATSAPP O INSTAGRAM no se puede instalar NADA, y es por
 *    donde llega la gente, porque esta aplicación se comparte por WhatsApp.
 *    Ahí lo que corresponde no es pedir que instalen —no tienen cómo— sino
 *    mandarlos a abrir el enlace en el navegador. Sin esa tercera pantalla,
 *    el bloqueo sería una puerta cerrada sin llave.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  RUTA_SIN_PUERTA,
  entornoDelNavegador,
  quéPedir,
  recordarQuePasó,
  type QuéPedir,
} from '../lib/instalacion';

/**
 * El evento de Chrome que permite pedir la instalación. No está en los tipos
 * del navegador porque no es estándar: lo implementa Chrome y nadie más.
 */
interface EventoDeInstalar extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstalarPrimero({ children }: { children: JSX.Element }): JSX.Element {
  const { t } = useTranslation();
  /*
   * `null` MIENTRAS NO SE MIRÓ. Arrancar en «hay que instalar» haría
   * parpadear la puerta en la cara de todo el mundo —incluida la
   * computadora— durante el primer dibujo.
   */
  const [pedir, setPedir] = useState<QuéPedir | 'SIN_MIRAR'>('SIN_MIRAR');
  const [evento, setEvento] = useState<EventoDeInstalar | null>(null);

  useEffect(() => {
    /*
     * `/mobile` ES LA SALIDA, y lo primero que hace es dejarlo anotado.
     *
     * Sin anotarlo, `/mobile` mostraría el login y la puerta volvería al primer
     * toque en «Clubes»: la dirección no serviría para nada. Se lee de
     * `window.location` y no del router porque esto corre antes que
     * cualquier ruta — es lo que envuelve al `<App/>`, no algo adentro de él.
     *
     * NO SE REDIRIGE a la raíz: quien guardó `/mobile` en su pantalla de inicio
     * tiene que poder abrirla siempre, y una redirección le cambiaría el
     * enlace guardado por uno que sí tiene puerta.
     */
    if (window.location.pathname === RUTA_SIN_PUERTA) recordarQuePasó();
    setPedir(quéPedir(entornoDelNavegador()));
  }, []);

  useEffect(() => {
    function guardar(event: Event): void {
      // `preventDefault` para que Chrome no muestre su propia barrita: la
      // instalación se pide desde el botón de esta pantalla.
      event.preventDefault();
      setEvento(event as EventoDeInstalar);
    }
    window.addEventListener('beforeinstallprompt', guardar);

    /*
     * Y CUANDO SE INSTALA, LA PUERTA SE ABRE SOLA. En Android la pestaña del
     * navegador queda abierta detrás de la app recién instalada; sin esto,
     * quien vuelve a esa pestaña sigue viendo «instalá la app» después de
     * haberla instalado.
     */
    function instalada(): void {
      setPedir(null);
    }
    window.addEventListener('appinstalled', instalada);

    return () => {
      window.removeEventListener('beforeinstallprompt', guardar);
      window.removeEventListener('appinstalled', instalada);
    };
  }, []);

  if (pedir === 'SIN_MIRAR' || pedir === null) return children;

  return (
    <main className="instalar-primero">
      <img className="instalar-icono" src="/brand/app-icon-192.png" alt="" width={96} height={96} />
      <h1>{t('install.title')}</h1>

      {pedir === 'ABRIR_EN_NAVEGADOR' ? (
        <>
          <p>{t('install.inAppBrowser')}</p>
          <ol className="instalar-pasos">
            <li>{t('install.inAppStep1')}</li>
            <li>{t('install.inAppStep2')}</li>
          </ol>
        </>
      ) : pedir === 'IOS' ? (
        <>
          <p>{t('install.iosLead')}</p>
          <ol className="instalar-pasos">
            <li>{t('install.iosStep1')}</li>
            <li>{t('install.iosStep2')}</li>
            <li>{t('install.iosStep3')}</li>
          </ol>
        </>
      ) : (
        <>
          <p>{t('install.androidLead')}</p>
          {evento === null ? (
            /* SIN EVENTO NO HAY BOTÓN, HAY INSTRUCTIVO. Dibujar un botón que
               no puede hacer nada es prometer algo que no se cumple. */
            <ol className="instalar-pasos">
              <li>{t('install.androidStep1')}</li>
              <li>{t('install.androidStep2')}</li>
            </ol>
          ) : (
            <button
              type="button"
              className="instalar-boton"
              onClick={() => {
                void evento.prompt().then(async () => {
                  const { outcome } = await evento.userChoice;
                  // El evento sirve UNA sola vez: Chrome no lo deja repetir.
                  // Guardarlo usado dejaría un botón que ya no hace nada.
                  setEvento(null);
                  if (outcome === 'accepted') setPedir(null);
                });
              }}
            >
              {t('install.androidButton')}
            </button>
          )}
        </>
      )}

      <p className="help instalar-porque">{t('install.why')}</p>
    </main>
  );
}
